import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { Prod01 } from '../../constants/ActionType';
import { useSortableTable, SortableTh, sortRows, nameFromString } from '../SortableTable';

const GAP_SORT_COLUMNS = {
    patient: { type: 'name', value: (it) => nameFromString(it?.patientName) },
    program: { type: 'string', value: (it) => it?.program },
    minutes: { type: 'number', value: (it) => it?.minutes },
    codes: { type: 'string', value: (it) => it?.codeSummary },
};

// Billing-gap alerts: minutes to the next billable CPT unit. Coaching to capture care actually performed and
// documented - never to add time. The platform flags opportunities only; it never files claims.
const COLORS = {
    red: { bg: '#fdecea', fg: '#b71c1c', label: 'below first threshold' },
    amber: { bg: '#fff4e0', fg: '#8a5a00', label: 'within 10 min of next unit' },
    green: { bg: '#e8f5e9', fg: '#1b5e20', label: 'threshold met / cap reached' },
};

// Highlighted-alert pulse (rule 18): 1 glow/sec (<= 3 flashes/sec, WCAG 2.3.1), 3 cycles then stops,
// stops on hover, and is disabled entirely under prefers-reduced-motion.
const PULSE_CSS = `
@keyframes vpGapPulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(240, 160, 0, 0); } 50% { box-shadow: 0 0 6px 3px rgba(240, 160, 0, 0.55); } }
.vp-gap-pulse { animation: vpGapPulse 1s ease-in-out 3; }
.vp-gap-pulse:hover, .vp-gap-pulse-stopped { animation: none !important; }
@media (prefers-reduced-motion: reduce) { .vp-gap-pulse { animation: none !important; } }
`;
export const PulseStyle = () => <style>{PULSE_CSS}</style>;
const isHighlighted = (it, lastWeek) => it.color === 'amber' || (lastWeek && it.color === 'red');

const profilePath = (role) => {
    if (role === 'admin' || role === 'adminStaff' || role === 'billing' || role === 'accounting') return '/patientProfile';
    if (role === 'doctor') return '/doctor/patient/profile';
    return '/Nurse/Patient/Profile';
};

const Dot = ({ color }) => {
    const c = COLORS[color] || COLORS.green;
    return <span title={c.label} style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: c.fg, marginRight: 6 }} />;
};

const GapText = ({ gap }) => (
    <div style={{ fontSize: 12 }}>
        <b>{gap.minutesToNext} min</b> to <b>{gap.nextCode}</b>
        <div className="text-muted" style={{ fontSize: 11 }}>{gap.text}</div>
    </div>
);

/** Small badge/notification count of open (red/amber) gaps for the caller's visible patients this month. */
export const BillingGapsBadge = ({ style }) => {
    const [data, setData] = useState(null);
    useEffect(() => {
        let alive = true;
        axios.get(`${Prod01}/billing/gaps/count`).then((r) => alive && setData(r.data)).catch(() => alive && setData(null));
        return () => { alive = false; };
    }, []);
    if (!data || !data.count) return null;
    return (
        <span className="badge bg-danger ms-1" title={`${data.count} billing gap${data.count === 1 ? '' : 's'} this month (${data.daysLeft} days left)`} style={style}>
            {data.count}
        </span>
    );
};

const BillingGapsWorklist = ({ role, title = 'Billing gaps this month' }) => {
    const [year, setYear] = useState(moment().year());
    const [month, setMonth] = useState(moment().month() + 1);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    const gapSort = useSortableTable([], { id: 'billing.gapsWorklist', columns: GAP_SORT_COLUMNS, manual: true, defaultSort: { key: 'patient', dir: 'asc' } });
    const load = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const r = await axios.get(`${Prod01}/billing/gaps?year=${year}&month=${month}`);
            setData(r.data);
        } catch (e) { setError(e?.response?.data?.message || e.message); } finally { setLoading(false); }
    }, [year, month]);

    useEffect(() => { load(); }, [load]);

    const shift = (n) => {
        const m = moment({ year, month: month - 1 }).add(n, 'months');
        setYear(m.year()); setMonth(m.month() + 1);
    };

    const items = data?.items || [];
    const final = !!data?.closed;
    const openItems = final ? [] : items;
    const finalItems = final ? items : [];
    const lastWeek = !!data?.lastWeek;
    const [stopped, setStopped] = useState({});
    const stop = (key) => setStopped((s) => (s[key] ? s : { ...s, [key]: true }));

    const row = (it) => {
        const c = COLORS[it.color] || COLORS.green;
        return (
            <tr
                key={`${it.patientId}-${it.program}`}
                style={{ background: c.bg }}
                className={!final && isHighlighted(it, lastWeek) ? `vp-gap-pulse${stopped[`${it.patientId}-${it.program}`] ? ' vp-gap-pulse-stopped' : ''}` : undefined}
                onMouseEnter={() => stop(`${it.patientId}-${it.program}`)}
            >
                <td style={{ whiteSpace: 'nowrap' }}>
                    <Dot color={it.color} />
                    <Link to={{ pathname: profilePath(role), state: { patientid: it.patientId } }} className="link">{it.patientName || it.emrId || it.patientId}</Link>
                    {it.emrId ? <small className="text-muted ms-1">#{it.emrId}</small> : null}
                </td>
                <td><b>{it.program}</b>{it.track ? <small className="text-muted"> ({it.track}{it.complexAttested ? ', complex attested' : ''})</small> : null}</td>
                <td>{it.minutes} min</td>
                <td style={{ fontSize: 12 }}>{it.codeSummary || <span className="text-muted">no code yet</span>}</td>
                <td>
                    {(it.gaps || []).length ? it.gaps.map((g) => <GapText key={g.nextCode} gap={g} />) : <span style={{ color: c.fg, fontSize: 12 }}>{final ? 'final' : c.label}</span>}
                    {it.deviceGap ? <div style={{ fontSize: 11 }} className="text-muted">{it.deviceGap.daysToNext} device day{it.deviceGap.daysToNext === 1 ? '' : 's'} to {it.deviceGap.nextCode} ({it.deviceDays} so far)</div> : null}
                </td>
                <td style={{ fontSize: 11 }}>
                    {it.needsInteractiveContact ? <span className="badge bg-warning text-dark me-1">needs interactive contact</span> : null}
                    {it.noteNotYetLocked ? <span className="badge bg-info text-dark me-1" title="A supporting clinical note for this month is still open (within its 72h window); informational only.">note not yet locked</span> : null}
                    {it.reviewPrompt ? <span className="badge bg-secondary me-1" title={it.reviewPrompt}>complexity review</span> : null}
                    {(it.flags || []).filter((f) => !/NOTE_NOT_LOCKED|NEEDS_INTERACTIVE/.test(f)).map((f) => <span key={f} className="badge bg-light text-dark border me-1">{f}</span>)}
                </td>
            </tr>
        );
    };

    const table = (rows, heading) => (
        <div className="mb-2">
            {heading ? <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{heading}</div> : null}
            <table className="table table-sm mb-1" style={{ fontSize: 13 }}>
                <thead>
                    <tr><SortableTh sortKey="patient" sort={gapSort}>Patient</SortableTh><SortableTh sortKey="program" sort={gapSort}>Program</SortableTh><SortableTh sortKey="minutes" sort={gapSort}>Minutes MTD</SortableTh><SortableTh sortKey="codes" sort={gapSort}>Current codes</SortableTh><th>Gap to next unit</th><th>Flags</th></tr>
                </thead>
                <tbody>
                    {rows.length ? (gapSort.sortKey ? sortRows(rows, GAP_SORT_COLUMNS[gapSort.sortKey], gapSort.sortDir) : rows).map(row) : <tr><td colSpan={6} className="text-muted">No gaps to show.</td></tr>}
                </tbody>
            </table>
        </div>
    );

    return (
        <div
            className={`shadow-lg p-3 mb-4${lastWeek && !final ? ` vp-gap-pulse${stopped.__panel ? ' vp-gap-pulse-stopped' : ''}` : ''}`}
            style={{ background: '#fff', borderRadius: 8, border: lastWeek ? '2px solid #f0a000' : '1px solid #eee' }}
            onMouseEnter={() => stop('__panel')}
        >
            <PulseStyle />
            <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
                <h5 className="mb-0" style={{ fontWeight: 700 }}>{title}</h5>
                {data ? (
                    <span className={`badge ${final ? 'bg-secondary' : data.openGaps ? 'bg-danger' : 'bg-success'}`}>
                        {final ? 'final' : `${data.openGaps} open`}
                    </span>
                ) : null}
                <span className="ms-auto" style={{ fontSize: 12 }}>
                    <button className="btn btn-sm btn-light" onClick={() => shift(-1)}>&lsaquo;</button>
                    <b className="mx-2">{moment({ year, month: month - 1 }).format('MMMM YYYY')}</b>
                    <button className="btn btn-sm btn-light" onClick={() => shift(1)} disabled={moment({ year, month: month - 1 }).isSameOrAfter(moment(), 'month')}>&rsaquo;</button>
                </span>
            </div>
            {data && !final ? (
                <div className="mt-1" style={{ fontSize: 12, color: lastWeek ? '#8a5a00' : '#555', fontWeight: lastWeek ? 700 : 400 }}>
                    {data.daysLeft} day{data.daysLeft === 1 ? '' : 's'} left in month{lastWeek ? ' - last 7 days: gaps become final at month close' : ''}
                </div>
            ) : null}
            {data && final ? <div className="mt-1 text-muted" style={{ fontSize: 12 }}>Month closed - gaps are final.</div> : null}
            <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                <Dot color="red" />below first threshold&nbsp;&nbsp;<Dot color="amber" />within 10 min of next unit&nbsp;&nbsp;<Dot color="green" />threshold met / cap reached
                &nbsp;&middot;&nbsp;Sorted smallest gap first. Opportunities only - not claims; capture only if clinically appropriate.
            </div>
            {error ? <div className="alert alert-warning mt-2 mb-0 py-1" style={{ fontSize: 12 }}>{error}</div> : null}
            {loading && !data ? <div className="text-muted mt-2" style={{ fontSize: 12 }}>Loading...</div> : null}
            {data ? (
                <div className="mt-2" style={{ maxHeight: 420, overflowY: 'auto' }}>
                    {final ? table(finalItems, 'Non-billable (final)') : table(openItems)}
                    {data.truncated ? <div className="text-muted" style={{ fontSize: 11 }}>List truncated; use the billing report for the full month.</div> : null}
                </div>
            ) : null}
            {data ? <div className="text-muted mt-1" style={{ fontSize: 11, fontStyle: 'italic' }}>{data.disclaimer}</div> : null}
        </div>
    );
};

export default BillingGapsWorklist;
